import { setup, assign, fromPromise, emit } from "xstate";
import { kml } from "@tmcw/togeojson";
import bbox from "@turf/bbox";
import { createActorContext } from "@xstate/react";
import * as zipjs from "@zip.js/zip.js";
import {
  MachineContext,
  FileProcessingEvent,
  ProcessedGeometry,
  GeometryFileType,
  type ShapefileGeometry,
  type GeoJSONGeometry,
  type KMLGeometry,
  type CSVGeometry,
  type PDFGeometry,
  type XLSXGeometry,
  isProcessedFileUploaded,
} from "../types";
import { generateUniqueFileIdentifier } from "../uploadMachine";
import { createUploads, deleteUploads } from "../uploadMachine/services";
import {
  getBlobFromZipEntry,
  extractShapefileFromZip,
  gatherShapefileComponents,
  createShapefileUpload,
  addOrUpdateShapefileInOutput,
  shouldUpdateExistingShapefile,
  readShapefile,
  computeMissingFlag,
} from "./lib/shapefile";

interface GeometryContext extends MachineContext<ProcessedGeometry> {
  processing: string[];
  error: string | null;
}

type GeometryEvent = FileProcessingEvent;

async function processShapefileComponentFiles(
  file: File,
  extension: GeometryFileType,
  name: string,
  allInputFiles: File[],
  context: GeometryContext,
  output: ProcessedGeometry[]
): Promise<void> {
  try {
    let componentFiles: File[] = [];

    if (extension === "zip") {
      try {
        componentFiles = await extractShapefileFromZip(file, name);
      } catch (error) {
        throw new Error(
          `Failed to extract shapefile from ZIP file ${file.name}: ${
            error instanceof Error ? error.message : String(error)
          }`
        );
      }
    } else if (extension === "shp") {
      componentFiles = [
        file,
        ...gatherShapefileComponents(name, allInputFiles),
      ];
    } else {
      componentFiles = [file];
    }

    for (const componentFile of componentFiles) {
      const componentExt = componentFile.name
        .toLowerCase()
        .split(".")
        .pop() as GeometryFileType;

      const existingShapefile = context.uploads.find(
        (upload) => upload.name === name && upload.type === "shp"
      ) as ShapefileGeometry | undefined;

      if (shouldUpdateExistingShapefile(existingShapefile, componentExt)) {
        addOrUpdateShapefileInOutput(
          output,
          existingShapefile,
          componentFile,
          componentExt
        );
        continue;
      }

      if (componentExt === "shp" && !existingShapefile) {
        output.push(createShapefileUpload(componentFile, name, componentFiles));
      }
    }
  } catch (error) {
    throw new Error(
      `Failed to process shapefile ${file.name}: ${
        error instanceof Error ? error.message : String(error)
      }`
    );
  }
}

function processGeometryFile(
  file: File,
  extension: GeometryFileType,
  name: string,
  context: GeometryContext,
  output: ProcessedGeometry[]
): void {
  const id = generateUniqueFileIdentifier(file).name;

  const existingUpload = context.uploads.find(
    (upload) => upload.id === id && isProcessedFileUploaded(upload)
  );

  if (!existingUpload) {
    const newUpload: ProcessedGeometry = {
      id,
      file,
      name: name,
      files: {
        [extension]: file,
      },
      type: extension as GeometryFileType,
      progress: 0,
    } as ProcessedGeometry;

    output.push({
      ...newUpload,
      missing: computeMissingFlag(newUpload),
    });
  }
}

function processAccessoryFile(
  file: File,
  extension: GeometryFileType,
  name: string,
  context: GeometryContext,
  output: ProcessedGeometry[]
): void {
  const id = generateUniqueFileIdentifier(file).name;

  const existingUpload = context.uploads.find(
    (upload) => upload.id === id && isProcessedFileUploaded(upload)
  );

  if (!existingUpload) {
    const newUpload: ProcessedGeometry = {
      id,
      file,
      name: name,
      files: {
        [extension]: file,
      },
      type: extension as GeometryFileType,
      progress: 1,
    } as ProcessedGeometry;

    output.push({
      ...newUpload,
      missing: computeMissingFlag(newUpload),
    });
  }
}

async function identifyGeometry({
  input,
}: {
  input: { files: File[]; context?: GeometryContext };
}) {
  const output: ProcessedGeometry[] = [];
  const context = input.context;

  for (const file of input.files) {
    const extension = file.name
      .toLowerCase()
      .split(".")
      .pop() as GeometryFileType;
    const name = file.name.split(".")[0];

    if (["shp", "shx", "dbf", "prj", "zip"].includes(extension)) {
      await processShapefileComponentFiles(
        file,
        extension,
        name,
        input.files,
        context,
        output
      );
    } else if (["kml", "kmz", "geojson"].includes(extension)) {
      processGeometryFile(file, extension, name, context, output);
    } else if (["csv", "pdf", "xlsx"].includes(extension)) {
      processAccessoryFile(file, extension, name, context, output);
    }
  }

  return output;
}

async function readGeoJSON({ input }: { input: { geojson: File } }) {
  const collection = JSON.parse(
    await input.geojson?.text()
  ) as GeoJSON.FeatureCollection;

  return {
    collection,
    bbox: bbox(collection),
  };
}

async function readKML({ input }: { input: { kml?: File; kmz?: File } }) {
  try {
    if (input.kmz) {
      let reader: zipjs.ZipReader<zipjs.Entry> | null = null;

      try {
        reader = new zipjs.ZipReader(new zipjs.BlobReader(input.kmz));
        const entries = await reader.getEntries();

        if (!entries || entries.length === 0) {
          throw new Error("KMZ file appears to be empty or corrupted");
        }

        const doc = entries.find(
          (entry: zipjs.Entry) => entry.filename === "doc.kml"
        );

        if (!doc) {
          throw new Error("doc.kml file not found in KMZ archive");
        }

        const blob = await getBlobFromZipEntry(doc);

        if (!blob) {
          throw new Error("Failed to extract doc.kml from KMZ archive");
        }

        const kmlText = await blob.text();

        if (!kmlText || kmlText.trim().length === 0) {
          throw new Error("doc.kml file is empty or corrupted");
        }

        let dom: Document;
        try {
          dom = new DOMParser().parseFromString(kmlText, "text/xml");
        } catch (parseError) {
          throw new Error(
            `Failed to parse KML XML: ${
              parseError instanceof Error
                ? parseError.message
                : String(parseError)
            }`
          );
        }

        const parserError = dom.querySelector("parsererror");
        if (parserError) {
          throw new Error("KML file contains XML parsing errors");
        }

        let collection: GeoJSON.FeatureCollection;
        try {
          collection = kml(dom) as GeoJSON.FeatureCollection;
        } catch (kmlError) {
          throw new Error(
            `Failed to convert KML to GeoJSON: ${
              kmlError instanceof Error ? kmlError.message : String(kmlError)
            }`
          );
        }

        if (
          !collection ||
          !collection.type ||
          collection.type !== "FeatureCollection"
        ) {
          throw new Error("KML conversion resulted in invalid GeoJSON");
        }

        return {
          collection,
          bbox: bbox(collection),
        };
      } finally {
        if (reader) {
          await reader.close();
        }
      }
    }

    if (input.kml) {
      const kmlText = await input.kml.text();

      if (!kmlText || kmlText.trim().length === 0) {
        throw new Error("KML file is empty or corrupted");
      }

      let dom: Document;
      try {
        dom = new DOMParser().parseFromString(kmlText, "text/xml");
      } catch (parseError) {
        throw new Error(
          `Failed to parse KML XML: ${
            parseError instanceof Error
              ? parseError.message
              : String(parseError)
          }`
        );
      }

      const parserError = dom.querySelector("parsererror");
      if (parserError) {
        throw new Error("KML file contains XML parsing errors");
      }

      let collection: GeoJSON.FeatureCollection;
      try {
        collection = kml(dom) as GeoJSON.FeatureCollection;
      } catch (kmlError) {
        throw new Error(
          `Failed to convert KML to GeoJSON: ${
            kmlError instanceof Error ? kmlError.message : String(kmlError)
          }`
        );
      }

      if (
        !collection ||
        !collection.type ||
        collection.type !== "FeatureCollection"
      ) {
        throw new Error("KML conversion resulted in invalid GeoJSON");
      }

      return {
        collection,
        bbox: bbox(collection),
      };
    }

    throw new Error("No KML or KMZ file found");
  } catch (error) {
    throw new Error(
      `Failed to read KML/KMZ: ${
        error instanceof Error ? error.message : String(error)
      }`
    );
  }
}

async function createGeometryUploads({
  input,
}: {
  input: {
    geometryFiles: ProcessedGeometry[];
    surveyId: number;
    orderId: number;
  };
}) {
  const { geometryFiles, surveyId, orderId } = input;

  if (geometryFiles.length === 0) {
    return { created: [] };
  }

  const uploadObjects = [];

  for (const geometry of geometryFiles) {
    if (geometry.type === "shp") {
      const shapefileComponents = [
        { file: geometry.files.shp, extension: "shp" },
        { file: geometry.files.prj, extension: "prj" },
        { file: geometry.files.shx, extension: "shx" },
        { file: geometry.files.dbf, extension: "dbf" },
      ];

      for (const component of shapefileComponents) {
        if (component.file) {
          const uniqueId = generateUniqueFileIdentifier(component.file);
          uploadObjects.push({
            key: uniqueId.name,
            fileName: geometry.name + "." + component.extension,
            fileSize: component.file.size,
            fileType: component.file.type || "application/octet-stream",
            fileModifiedAt: component.file.lastModified,
            fileExtension: component.extension,
            fileMetadata: geometry.data?.bbox
              ? { bbox: geometry.data.bbox }
              : {},
            surveyId,
            orderId,
          });
        }
      }
    } else {
      let fileSize = 0;
      let fileType = "application/octet-stream";
      let fileModifiedAt = Date.now();

      if (geometry.type === "geojson" && "geojson" in geometry.files) {
        fileSize = geometry.files.geojson?.size || 0;
        fileType = geometry.files.geojson?.type || "application/octet-stream";
        fileModifiedAt = geometry.files.geojson?.lastModified || Date.now();
      } else if (
        (geometry.type === "kml" || geometry.type === "kmz") &&
        ("kml" in geometry.files || "kmz" in geometry.files)
      ) {
        const file = geometry.files.kml || geometry.files.kmz;
        fileSize = file?.size || 0;
        fileType = file?.type || "application/octet-stream";
        fileModifiedAt = file?.lastModified || Date.now();
      } else if (geometry.type === "csv" && "csv" in geometry.files) {
        fileSize = geometry.files.csv?.size || 0;
        fileType = geometry.files.csv?.type || "text/csv";
        fileModifiedAt = geometry.files.csv?.lastModified || Date.now();
      } else if (geometry.type === "pdf" && "pdf" in geometry.files) {
        fileSize = geometry.files.pdf?.size || 0;
        fileType = geometry.files.pdf?.type || "application/pdf";
        fileModifiedAt = geometry.files.pdf?.lastModified || Date.now();
      } else if (geometry.type === "xlsx" && "xlsx" in geometry.files) {
        fileSize = geometry.files.xlsx?.size || 0;
        fileType =
          geometry.files.xlsx?.type ||
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
        fileModifiedAt = geometry.files.xlsx?.lastModified || Date.now();
      }

      uploadObjects.push({
        key: geometry.id,
        fileName:
          geometry.name +
          "." +
          (geometry.type === "geojson"
            ? "geojson"
            : geometry.type === "kml"
            ? "kml"
            : geometry.type === "kmz"
            ? "kmz"
            : geometry.type === "csv"
            ? "csv"
            : geometry.type === "xlsx"
            ? "xlsx"
            : "pdf"),
        fileSize,
        fileType,
        fileModifiedAt,
        fileExtension:
          geometry.type === "geojson"
            ? "geojson"
            : geometry.type === "kml"
            ? "kml"
            : geometry.type === "kmz"
            ? "kmz"
            : geometry.type === "csv"
            ? "csv"
            : geometry.type === "xlsx"
            ? "xlsx"
            : "pdf",
        fileMetadata: geometry.data?.bbox ? { bbox: geometry.data.bbox } : {},
        surveyId,
        orderId,
      });
    }
  }

  const created = await createUploads({
    input: uploadObjects,
  });

  return { created };
}

async function deleteGeometryUploads({
  input,
}: {
  input: {
    keys: string[];
    geometryFiles?: ProcessedGeometry[];
    surveyId: number;
    orderId: number;
  };
}) {
  const { keys, geometryFiles, surveyId, orderId } = input;

  if (keys.length === 0) {
    return { deleted: [] };
  }

  let allKeysToDelete = [...keys];

  if (geometryFiles) {
    for (const geometry of geometryFiles) {
      if (geometry.type === "shp" && geometry.componentKeys) {
        const componentKeys = [
          geometry.componentKeys.shp,
          geometry.componentKeys.prj,
          geometry.componentKeys.shx,
          geometry.componentKeys.dbf,
        ].filter(Boolean); // Remove undefined values

        allKeysToDelete.push(...componentKeys);
      }
    }
  }

  allKeysToDelete = [...new Set(allKeysToDelete)];

  const deleted = await deleteUploads({
    input: { keys: allKeysToDelete, surveyId, orderId },
  });

  return { deleted };
}

export const createGeometryMachine = setup({
  types: {} as {
    context: GeometryContext;
    events: GeometryEvent;
    input: { existingUploads?: any[]; surveyId?: number; orderId?: number };
    emitted: { type: "focusOnGeometry" };
  },
  actors: {
    identifyGeometry: fromPromise(identifyGeometry),
    readShapefile: fromPromise(readShapefile),
    readGeoJSON: fromPromise(readGeoJSON),
    readKML: fromPromise(readKML),
    createGeometryUploads: fromPromise(createGeometryUploads),
    deleteGeometryUploads: fromPromise(deleteGeometryUploads),
  },
}).createMachine({
  id: "manageGeometry",
  initial: "idle",
  context: ({ input }) => {
    if (!input?.existingUploads) {
      return {
        uploads: [],
        active: null,
        processing: [],
        removing: null,
        surveyId: input?.surveyId ?? 0,
        orderId: input?.orderId ?? 0,
        error: null,
      } as GeometryContext;
    }

    const shapefileGroups = new Map<string, any[]>();
    const nonShapefileUploads: any[] = [];

    for (const upload of input.existingUploads) {
      if (["shp", "prj", "shx", "dbf"].includes(upload.file_extension)) {
        const name = upload.file_name.replace(/\.(shp|prj|shx|dbf)$/i, "");
        if (!shapefileGroups.has(name)) {
          shapefileGroups.set(name, []);
        }
        shapefileGroups.get(name)!.push(upload);
      } else {
        nonShapefileUploads.push(upload);
      }
    }

    const reconstructedUploads: ProcessedGeometry[] = [];

    for (const [name, components] of shapefileGroups) {
      const shpComponent = components.find((c) => c.file_extension === "shp");
      const prjComponent = components.find((c) => c.file_extension === "prj");
      const shxComponent = components.find((c) => c.file_extension === "shx");
      const dbfComponent = components.find((c) => c.file_extension === "dbf");

      const primaryComponent = shpComponent || components[0];

      const shapefileUpload: ShapefileGeometry = {
        id: primaryComponent.key,
        name: name,
        type: "shp",
        componentKeys: {
          shp: shpComponent?.key,
          prj: prjComponent?.key,
          shx: shxComponent?.key,
          dbf: dbfComponent?.key,
        },
        file: {
          size: primaryComponent.file_size,
          name: primaryComponent.file_name,
          type: primaryComponent.file_type,
          lastModified: primaryComponent.file_modified_at,
        },
        files: {
          shp: shpComponent
            ? {
                size: shpComponent.file_size,
                name: shpComponent.file_name + ".shp",
                type: shpComponent.file_type,
                lastModified: shpComponent.file_modified_at,
                status: shpComponent.status,
              }
            : undefined,
          prj: prjComponent
            ? {
                size: prjComponent.file_size,
                name: prjComponent.file_name + ".prj",
                type: prjComponent.file_type,
                lastModified: prjComponent.file_modified_at,
                status: prjComponent.status,
              }
            : undefined,
          shx: shxComponent
            ? {
                size: shxComponent.file_size,
                name: shxComponent.file_name + ".shx",
                type: shxComponent.file_type,
                lastModified: shxComponent.file_modified_at,
                status: shxComponent.status,
              }
            : undefined,
          dbf: dbfComponent
            ? {
                size: dbfComponent.file_size,
                name: dbfComponent.file_name + ".dbf",
                type: dbfComponent.file_type,
                lastModified: dbfComponent.file_modified_at,
                status: dbfComponent.status,
              }
            : undefined,
        },
        data: primaryComponent.file_metadata,
      };

      reconstructedUploads.push({
        ...shapefileUpload,
        missing: computeMissingFlag(shapefileUpload),
      });
    }

    for (const upload of nonShapefileUploads) {
      const processedUpload = (() => {
        const baseFile =
          upload.status === "uploaded" &&
          upload.file_size > 0 &&
          upload.file_name &&
          upload.file_type
            ? {
                size: upload.file_size,
                name: upload.file_name,
                type: upload.file_type,
                lastModified: upload.file_modified_at,
                status: "uploaded" as const,
              }
            : upload.file_size > 0 && upload.file_name && upload.file_type
            ? {
                size: upload.file_size,
                name: upload.file_name,
                type: upload.file_type,
                lastModified: upload.file_modified_at,
              }
            : undefined;

        switch (upload.file_extension) {
          case "geojson":
            return {
              id: upload.key,
              name: upload.file_name,
              type: "geojson" as const,
              file: {
                size: upload.file_size,
                name: upload.file_name,
                type: upload.file_type,
                lastModified: upload.file_modified_at,
                status:
                  upload.status === "uploaded"
                    ? ("uploaded" as const)
                    : undefined,
              },
              files: { geojson: baseFile } as const,
              data: upload.file_metadata,
            } as GeoJSONGeometry;
          case "kml":
            return {
              id: upload.key,
              name: upload.file_name,
              type: "kml" as const,
              file: {
                size: upload.file_size,
                name: upload.file_name,
                type: upload.file_type,
                lastModified: upload.file_modified_at,
                status:
                  upload.status === "uploaded"
                    ? ("uploaded" as const)
                    : undefined,
              },
              files: { kml: baseFile } as const,
              data: upload.file_metadata,
            } as KMLGeometry;
          case "kmz":
            return {
              id: upload.key,
              name: upload.file_name,
              type: "kmz" as const,
              file: {
                size: upload.file_size,
                name: upload.file_name,
                type: upload.file_type,
                lastModified: upload.file_modified_at,
                status:
                  upload.status === "uploaded"
                    ? ("uploaded" as const)
                    : undefined,
              },
              files: { kmz: baseFile } as const,
              data: upload.file_metadata,
            } as KMLGeometry;
          case "csv":
            return {
              id: upload.key,
              name: upload.file_name,
              type: "csv" as const,
              file: {
                size: upload.file_size,
                name: upload.file_name,
                type: upload.file_type,
                lastModified: upload.file_modified_at,
                status:
                  upload.status === "uploaded"
                    ? ("uploaded" as const)
                    : undefined,
              },
              files: { csv: baseFile } as const,
              data: upload.file_metadata,
            } as CSVGeometry;
          case "pdf":
            return {
              id: upload.key,
              name: upload.file_name,
              type: "pdf" as const,
              file: {
                size: upload.file_size,
                name: upload.file_name,
                type: upload.file_type,
                lastModified: upload.file_modified_at,
                status:
                  upload.status === "uploaded"
                    ? ("uploaded" as const)
                    : undefined,
              },
              files: { pdf: baseFile } as const,
              data: upload.file_metadata,
            } as PDFGeometry;
          case "xlsx":
            return {
              id: upload.key,
              name: upload.file_name,
              type: "xlsx" as const,
              file: {
                size: upload.file_size,
                name: upload.file_name,
                type: upload.file_type,
                lastModified: upload.file_modified_at,
                status:
                  upload.status === "uploaded"
                    ? ("uploaded" as const)
                    : undefined,
              },
              files: { xlsx: baseFile } as const,
              data: upload.file_metadata,
            } as XLSXGeometry;
          default:
            return {
              id: upload.key,
              name: upload.file_name,
              type: "kmz" as const,
              file: {
                size: upload.file_size,
                name: upload.file_name,
                type: upload.file_type,
                lastModified: upload.file_modified_at,
                status:
                  upload.status === "uploaded"
                    ? ("uploaded" as const)
                    : undefined,
              },
              files: { kmz: baseFile } as const,
              data: upload.file_metadata,
            } as KMLGeometry;
        }
      })();

      reconstructedUploads.push({
        ...processedUpload,
        missing: computeMissingFlag(processedUpload),
      });
    }

    return {
      uploads: reconstructedUploads,
      active: null,
      processing: [],
      removing: null,
      surveyId: input?.surveyId ?? 0,
      orderId: input?.orderId ?? 0,
      error: null,
    } as GeometryContext;
  },
  states: {
    idle: {
      on: {
        ADD_FILES: {
          target: "identify",
          guard: ({ event }: { event: { files: File[] } }) =>
            event.files.length > 0,
        },
        REMOVE_FILE: {
          target: "remove",
          actions: assign({
            removing: ({ context, event }) => event.id,
          }),
        },
        REMOVE_MISSING: {
          target: "removeMissing",
        },
        UPDATE_LABEL: {
          actions: assign({
            uploads: ({ context, event }) =>
              context.uploads.map((upload) =>
                upload.id === event.id
                  ? { ...upload, label: event.label }
                  : upload
              ),
          }),
        },
      },
    },
    remove: {
      initial: "confirm",
      states: {
        confirm: {
          on: {
            CONFIRM_REMOVE: { target: "removing" },
            CANCEL_REMOVE: { target: "#manageGeometry.idle" },
          },
        },
        removing: {
          invoke: {
            id: "deleteGeometryUploads",
            src: "deleteGeometryUploads",
            input: ({ context }) => ({
              keys: [context.removing],
              geometryFiles: context.uploads.filter(
                (u) => u.id === context.removing
              ),
              surveyId: context.surveyId,
              orderId: context.orderId,
            }),
            onDone: {
              target: "#manageGeometry.idle",
              actions: assign({
                removing: null,
                uploads: ({ context }) =>
                  context.uploads.filter((u) => u.id !== context.removing),
              }),
            },
            onError: {
              target: "#manageGeometry.idle",
              actions: assign({
                error: ({ event }) =>
                  (event.error as Error)?.message || String(event.error),
              }),
            },
          },
        },
      },
    },
    removeMissing: {
      initial: "confirm",
      states: {
        confirm: {
          on: {
            CONFIRM_REMOVE_MISSING: { target: "removing" },
            CANCEL_REMOVE: { target: "#manageGeometry.idle" },
          },
        },
        removing: {
          invoke: {
            id: "deleteMissingGeometryUploads",
            src: "deleteGeometryUploads",
            input: ({ context }) => ({
              keys: context.uploads
                .filter((upload) => upload.missing)
                .map((upload) => upload.id),
              geometryFiles: context.uploads.filter((upload) => upload.missing),
              surveyId: context.surveyId,
              orderId: context.orderId,
            }),
            onDone: {
              target: "#manageGeometry.idle",
              actions: assign({
                uploads: ({ context }) =>
                  context.uploads.filter((upload) => !upload.missing),
                removing: null,
              }),
            },
            onError: {
              target: "#manageGeometry.idle",
              actions: assign({
                removing: null,
              }),
            },
          },
        },
      },
    },
    identify: {
      invoke: {
        id: "identifyGeometry",
        src: "identifyGeometry",
        input: ({ event, context }) => {
          if (event.type === "ADD_FILES") {
            return { files: event.files, context };
          }
          throw new Error("Invalid event type");
        },
        onDone: [
          {
            guard: ({ event }) => event.output.length > 0,
            target: "processing",
            actions: assign({
              uploads: ({ context, event }) => {
                const outputMap = new Map(event.output.map((u) => [u.id, u]));

                return [
                  ...context.uploads.map((u) => {
                    const updated = outputMap.get(u.id) ?? u;
                    return {
                      ...updated,
                      missing: computeMissingFlag(updated),
                    };
                  }),
                  ...event.output.filter(
                    (u) =>
                      !context.uploads.some((existing) => existing.id === u.id)
                  ),
                ];
              },
              processing: ({ event }) =>
                event.output.map((geometry: ProcessedGeometry) => geometry.id),
            }),
          },
          {
            target: "idle",
          },
        ],
        onError: {
          target: "idle",
          actions: assign({
            error: ({ event }) =>
              (event.error as Error)?.message || String(event.error),
          }),
        },
      },
    },
    creating: {
      invoke: {
        id: "createGeometryUploads",
        src: "createGeometryUploads",
        input: ({ context }: { context: GeometryContext }) => {
          return {
            geometryFiles: context.uploads.filter((upload) =>
              context.processing.includes(upload.id)
            ),
            surveyId: context.surveyId,
            orderId: context.orderId,
          };
        },
        onDone: {
          target: "idle",
          actions: emit({ type: "focusOnGeometry" }),
        },
        onError: {
          target: "idle",
        },
      },
    },
    processing: {
      initial: "check",
      states: {
        check: {
          always: [
            {
              guard: ({ context }) => {
                return (
                  context.uploads.filter(
                    (upload) =>
                      !upload.data && !upload.error && upload.progress !== 1
                  ).length > 0
                );
              },
              target: "start",
            },
            { target: "#manageGeometry.creating" },
          ],
        },
        start: {
          entry: assign({
            active: ({ context }) =>
              context.uploads.filter(
                (upload) =>
                  !upload.data && !upload.error && upload.progress !== 1
              )[0]?.id,
          }),
          always: [
            {
              guard: ({ context }) => {
                return (
                  context.uploads.find((upload) => upload.id === context.active)
                    ?.type === "shp"
                );
              },
              target: "shapefile",
            },
            {
              guard: ({ context }) =>
                context.uploads.find((upload) => upload.id === context.active)
                  ?.type === "geojson",
              target: "geojson",
            },
            {
              guard: ({ context }) => {
                const upload = context.uploads.find(
                  (upload) => upload.id === context.active
                );

                return upload?.type === "kml" || upload?.type === "kmz";
              },
              target: "kml",
            },
          ],
        },
        shapefile: {
          invoke: {
            id: "readShapefile",
            src: "readShapefile",
            input: ({ context }) => {
              const upload = context.uploads.find(
                (upload) => upload.id === context.active
              );

              return {
                shp:
                  upload &&
                  "shp" in upload.files &&
                  upload.files.shp instanceof File
                    ? upload.files.shp
                    : undefined,
                dbf:
                  upload &&
                  "dbf" in upload.files &&
                  upload.files.dbf instanceof File
                    ? upload.files.dbf
                    : undefined,
                shx:
                  upload &&
                  "shx" in upload.files &&
                  upload.files.shx instanceof File
                    ? upload.files.shx
                    : undefined,
                prj:
                  upload &&
                  "prj" in upload.files &&
                  upload.files.prj instanceof File
                    ? upload.files.prj
                    : undefined,
              };
            },
            onDone: {
              target: "check",
              actions: assign({
                uploads: ({ context, event }) =>
                  context.uploads.map((upload) => {
                    if (upload.id === context.active) {
                      const updated = {
                        ...upload,
                        data: event.output,
                        progress: 1,
                      };
                      return {
                        ...updated,
                        missing: computeMissingFlag(updated),
                      };
                    }
                    return upload;
                  }),
                active: null,
              }),
            },
            onError: {
              target: "check",
              actions: assign({
                uploads: ({ context, event }) =>
                  context.uploads.map((upload) => {
                    if (upload.id === context.active) {
                      return {
                        ...upload,
                        error:
                          (event.error as Error)?.message ||
                          String(event.error),
                        progress: 0,
                      };
                    }
                    return upload;
                  }),
                active: null,
              }),
            },
          },
        },
        geojson: {
          invoke: {
            id: "readGeoJSON",
            src: "readGeoJSON",
            input: ({ context }) => {
              const upload = context.uploads.find(
                (upload) => upload.id === context.active
              );
              const geojsonFile =
                upload && "geojson" in upload.files
                  ? upload.files.geojson
                  : undefined;
              return {
                geojson: geojsonFile instanceof File ? geojsonFile : undefined,
              };
            },
            onDone: {
              target: "check",
              actions: assign({
                uploads: ({ context, event }) =>
                  context.uploads.map((upload) => {
                    if (upload.id === context.active) {
                      const updated = {
                        ...upload,
                        data: event.output,
                        progress: 1,
                      };
                      return {
                        ...updated,
                        missing: computeMissingFlag(updated),
                      };
                    }
                    return upload;
                  }),
                active: null,
              }),
            },
            onError: {
              target: "check",
              actions: assign({
                uploads: ({ context, event }) =>
                  context.uploads.map((upload) => {
                    if (upload.id === context.active) {
                      return {
                        ...upload,
                        error:
                          (event.error as Error)?.message ||
                          String(event.error),
                        progress: 0,
                      };
                    }
                    return upload;
                  }),
                active: null,
              }),
            },
          },
        },
        kml: {
          invoke: {
            id: "readKML",
            src: "readKML",
            input: ({ context }) => {
              const upload = context.uploads.find(
                (upload) => upload.id === context.active
              );

              const kmlFile =
                upload && "kml" in upload.files ? upload.files.kml : undefined;
              const kmzFile =
                upload && "kmz" in upload.files ? upload.files.kmz : undefined;

              return {
                kml: kmlFile instanceof File ? kmlFile : undefined,
                kmz: kmzFile instanceof File ? kmzFile : undefined,
              };
            },
            onDone: {
              target: "check",
              actions: assign({
                uploads: ({ context, event }) =>
                  context.uploads.map((upload) => {
                    if (upload.id === context.active) {
                      const updated = {
                        ...upload,
                        data: event.output,
                        progress: 1,
                      };
                      return {
                        ...updated,
                        missing: computeMissingFlag(updated),
                      };
                    }
                    return upload;
                  }),
                active: null,
              }),
            },
            onError: {
              target: "check",
              actions: assign({
                error: ({ event }) =>
                  (event.error as Error)?.message || String(event.error),
              }),
            },
          },
        },
      },
    },
  },
});

export const GeometryMachineContext = createActorContext(createGeometryMachine);
